import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Check, Crown, Loader2 } from 'lucide-react';

export default function Subscription() {
  const { axiosAuth, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollStatus(sessionId);
    }
  }, [searchParams]);

  const pollStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 5) {
      setPaymentResult({ status: 'timeout', message: 'Verificando pago. Revisa tu email.' });
      return;
    }
    setChecking(true);
    try {
      const res = await axiosAuth().get(`/subscriptions/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentResult({ status: 'success', message: 'Pago exitoso! Tu suscripcion esta activa.' });
        setChecking(false);
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  };

  const checkout = async (planId) => {
    setLoading(true);
    try {
      const res = await axiosAuth().post('/subscriptions/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al procesar pago');
      setLoading(false);
    }
  };

  const hasSub = user?.has_subscription || user?.subscription_status === 'active';

  return (
    <div className="space-y-8 animate-fade-in" data-testid="subscription-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Suscripcion</p>
        <h2 className="font-heading text-3xl font-bold">Tu Plan</h2>
      </div>

      {checking && (
        <div className="p-6 bg-gold/10 border border-gold/30 flex items-center gap-3" data-testid="payment-checking">
          <Loader2 size={20} className="text-gold animate-spin" />
          <p className="text-sm text-gold">Verificando tu pago...</p>
        </div>
      )}

      {paymentResult && (
        <div className={`p-6 border ${paymentResult.status === 'success' ? 'bg-emerald-400/10 border-emerald-400/30' : 'bg-amber-400/10 border-amber-400/30'}`} data-testid="payment-result">
          <div className="flex items-center gap-3">
            {paymentResult.status === 'success' ? <Check size={20} className="text-emerald-400" /> : <CreditCard size={20} className="text-amber-400" />}
            <p className="text-sm">{paymentResult.message}</p>
          </div>
        </div>
      )}

      {hasSub ? (
        <div className="bg-[#0A0A0A] border-2 border-gold p-8" data-testid="active-subscription">
          <div className="flex items-center gap-3 mb-4">
            <Crown size={24} className="text-gold" />
            <h3 className="font-heading text-2xl font-bold text-gold">Plan Professional Activo</h3>
          </div>
          <p className="text-sm text-white/50">Tienes acceso completo a todas las funcionalidades de M3.</p>
          <div className="mt-4 space-y-2">
            <p className="text-xs text-white/40 flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Ingredientes ilimitados</p>
            <p className="text-xs text-white/40 flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Recetas ilimitadas</p>
            <p className="text-xs text-white/40 flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Menu Engineering con IA</p>
            <p className="text-xs text-white/40 flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Alertas inteligentes</p>
            <p className="text-xs text-white/40 flex items-center gap-2"><Check size={12} className="text-emerald-400" /> Reportes premium</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="plans-grid">
          {/* Monthly */}
          <div className="p-8 bg-[#0A0A0A] border-2 border-gold relative" data-testid="plan-monthly-card">
            <div className="absolute -top-3 left-6 bg-gold text-black text-[10px] font-bold uppercase tracking-widest px-3 py-1">Recomendado</div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-4">Professional Mensual</p>
            <p className="font-heading text-5xl font-bold text-gold mb-1">$50</p>
            <p className="text-xs text-white/40 mb-8">USD / mes</p>
            <ul className="space-y-3 mb-8">
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> Todo ilimitado</li>
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> IA de precios y menu</li>
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> Reportes ejecutivos</li>
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> Alertas inteligentes</li>
            </ul>
            <button onClick={() => checkout('monthly')} disabled={loading} data-testid="checkout-monthly-btn"
              className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
              {loading ? 'Procesando...' : 'Suscribirse Ahora'}
            </button>
          </div>

          {/* Annual */}
          <div className="p-8 bg-[#0A0A0A] border border-white/10" data-testid="plan-annual-card">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">Professional Anual</p>
            <p className="font-heading text-5xl font-bold mb-1">$500</p>
            <p className="text-xs text-gold mb-8">USD / ano (ahorra $100)</p>
            <ul className="space-y-3 mb-8">
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> Todo lo del plan mensual</li>
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> 2 meses gratis</li>
              <li className="text-sm text-white/60 flex items-center gap-2"><Check size={12} className="text-gold" /> Soporte prioritario</li>
            </ul>
            <button onClick={() => checkout('annual')} disabled={loading} data-testid="checkout-annual-btn"
              className="w-full py-3 border border-white/20 text-xs font-bold uppercase tracking-widest hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
              {loading ? 'Procesando...' : 'Suscribirse Anual'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
